import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/core/dialog';
import { Button } from '../../components/core/button';
import { Separator } from '../../components/core/separator';
import { useUserType } from '../../feature-flags/use-user-type';

type TermsSection = {
  title: string;
  description: React.ReactNode;
};

export type TermsAndConditionsDialogProps = {
  open: boolean;
  onAccept: () => void;
  onDecline?: () => void;
  sections?: TermsSection[];
  highlights?: string[];
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  effectiveDate?: string;
  supportEmail?: string;
  acceptLabel?: string;
  isAccepting?: boolean;
};

const DEFAULT_HIGHLIGHTS = [
  'Respeito à diversidade',
  'Uso exclusivo',
  'Integridade acadêmica',
];

const DEFAULT_SECTIONS: TermsSection[] = [
  {
    title: '1. Objeto',
    description: (
      <>
        O presente Termo de Uso tem como objetivo regular o acesso e a utilização da plataforma digital da{' '}
        <strong>English2Empower</strong>, doravante denominada E2E, na qual estão hospedados materiais pedagógicos,
        conteúdos autorais, recursos didáticos, vídeos, textos, atividades, gravações de aulas e demais materiais
        desenvolvidos exclusivamente pela escola.
        <br />
        <br />
        Ao acessar a plataforma, a usuária declara ter lido, compreendido e concordado integralmente com os termos aqui
        estabelecidos.
      </>
    ),
  },
  {
    title: '2. Propriedade Intelectual',
    description: (
      <>
        Todo o conteúdo disponibilizado na plataforma da E2E é protegido pela legislação brasileira de direitos autorais e
        de propriedade intelectual, incluindo, mas não se limitando a materiais escritos, vídeos, áudios, apresentações,
        exercícios, metodologias, estruturas de curso e materiais complementares.
        <br />
        <br />
        É <strong>expressamente proibido</strong>:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            Copiar, reproduzir, distribuir, compartilhar, vender, sublicenciar ou divulgar, total ou parcialmente,
            qualquer conteúdo da plataforma, em qualquer formato ou meio, sem autorização prévia e expressa da
            English2Empower.
          </li>
          <li>
            Utilizar os materiais da plataforma para fins comerciais, educacionais ou institucionais fora do ambiente da
            E2E.
          </li>
          <li>Disponibilizar os conteúdos para pessoas que não sejam alunas regularmente matriculadas na escola.</li>
        </ul>
        <br />O uso dos materiais é <strong>exclusivo, pessoal e intransferível</strong>, destinado apenas à aluna
        cadastrada.
      </>
    ),
  },
  {
    title: '3. Expectativas de Uso e Convivência',
    description: (
      <>
        A English2Empower é uma comunidade de aprendizagem formada por mulheres com diferentes histórias, trajetórias e
        vivências. Ao utilizar a plataforma e participar das aulas, a aluna compromete-se a contribuir para um ambiente
        seguro, respeitoso e colaborativo.
        <br />
        <br />
        <strong>a) Respeito à diversidade e à convivência</strong>
        <br />
        Todas as interações devem ser pautadas pelo respeito à diversidade de opiniões, experiências, sotaques,
        identidades e percursos de aprendizagem. Não são toleradas atitudes discriminatórias, ofensivas ou que comprometam
        a segurança emocional da comunidade.
        <br />
        <br />
        <strong>b) Compromisso e constância no processo de aprendizagem</strong>
        <br />A aluna reconhece que aprender um idioma exige envolvimento ativo, estudo e prática contínua. O
        desenvolvimento linguístico depende de compromisso com as aulas, materiais e propostas pedagógicas, não existindo
        resultados imediatos ou fórmulas mágicas.
        <br />
        <br />
        <strong>c) Aprendizagem em comunidade</strong>
        <br />A E2E valoriza a aprendizagem coletiva como parte essencial do processo educacional. Espera-se uma postura
        colaborativa, respeitosa e solidária, que contribua para o crescimento individual e coletivo das alunas.
      </>
    ),
  },
  {
    title: '4. Integridade Acadêmica',
    description: (
      <>
        As atividades, exercícios, avaliações e feedbacks disponibilizados na plataforma da English2Empower são
        desenvolvidos para uso <strong>individual e exclusivo</strong> de cada aluna, de acordo com seu nível e percurso
        de aprendizagem.
        <br />
        <br />
        É proibido:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            Compartilhar respostas, atividades, exercícios, transcrições, feedbacks ou qualquer conteúdo avaliativo da
            plataforma com terceiros.
          </li>
          <li>Utilizar respostas ou materiais produzidos por outras alunas como se fossem próprios.</li>
          <li>Copiar, reproduzir ou redistribuir atividades avaliativas fora da plataforma.</li>
        </ul>
        <br />
        Essas práticas comprometem o aprendizado individual, o funcionamento pedagógico da escola e os princípios de
        honestidade e responsabilidade acadêmica da English2Empower.
      </>
    ),
  },
  {
    title: '5. Acesso à Plataforma e Uso de Login',
    description: (
      <>
        O acesso à plataforma da English2Empower é realizado por meio de login e senha individuais, criados de forma
        exclusiva para cada aluna cadastrada.
        <br />
        <br />
        É <strong>terminantemente proibido</strong>:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Compartilhar login, senha ou qualquer forma de acesso à plataforma com terceiros.</li>
          <li>
            Permitir que pessoas não matriculadas utilizem ou visualizem os conteúdos da plataforma por meio de seu
            acesso.
          </li>
        </ul>
        <br />
        Cada aluna é integralmente responsável pela confidencialidade de suas credenciais de acesso e por qualquer
        atividade realizada em sua conta.
      </>
    ),
  },
  {
    title: '6. Controle de Acesso e Monitoramento',
    description: (
      <>
        A English2Empower realiza o controle de acesso à plataforma por meio de <strong>logs individuais</strong>, que
        registram informações como data, horário e frequência de acesso de cada usuária.
        <br />
        <br />
        Esses registros têm como finalidade garantir a segurança da plataforma, proteger a propriedade intelectual da
        escola e verificar o cumprimento deste Termo de Uso.
        <br />
        <br />O compartilhamento de acessos poderá ser identificado por meio desses registros e caracterizado como
        violação contratual.
      </>
    ),
  },
  {
    title: '7. Sanções em Caso de Violação',
    description: (
      <>
        O descumprimento de qualquer cláusula deste Termo de Uso poderá resultar, a critério da English2Empower, nas
        seguintes medidas, aplicáveis de forma isolada ou cumulativa:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Bloqueio temporário ou definitivo do acesso à plataforma.</li>
          <li>Cancelamento da matrícula, sem direito a reembolso.</li>
          <li>
            Adoção de medidas legais cabíveis para proteção da propriedade intelectual e reparação de eventuais danos.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: '8. Disposições Finais',
    description: (
      <>
        A English2Empower reserva-se o direito de atualizar este Termo de Uso sempre que necessário, sendo responsabilidade
        da usuária manter-se informada sobre eventuais alterações.
        <br />
        <br />
        Este Termo é regido pelas leis da República Federativa do Brasil.
      </>
    ),
  },
];

// ---------------------------------------------------------------------------
// B2B "In Company" variant — same terms, neutral-professional wording.
// Shown only to `type === 'b2b'` users; everyone else keeps the default
// women-first sections above, untouched.
// ---------------------------------------------------------------------------

const B2B_SECTIONS: TermsSection[] = [
  {
    title: '1. Objeto',
    description: (
      <>
        O presente Termo de Uso tem como objetivo regular o acesso e a utilização da plataforma digital da{' '}
        <strong>English2Empower</strong>, doravante denominada E2E, na qual estão hospedados materiais pedagógicos,
        conteúdos autorais, recursos didáticos, vídeos, textos, atividades, gravações de aulas e demais materiais
        desenvolvidos exclusivamente pela escola.
        <br />
        <br />
        Ao acessar a plataforma, o(a) usuário(a) declara ter lido, compreendido e concordado integralmente com os termos
        aqui estabelecidos.
      </>
    ),
  },
  {
    title: '2. Propriedade Intelectual',
    description: (
      <>
        Todo o conteúdo disponibilizado na plataforma da E2E é protegido pela legislação brasileira de direitos autorais e
        de propriedade intelectual, incluindo, mas não se limitando a materiais escritos, vídeos, áudios, apresentações,
        exercícios, metodologias, estruturas de curso e materiais complementares.
        <br />
        <br />
        É <strong>expressamente proibido</strong>:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            Copiar, reproduzir, distribuir, compartilhar, vender, sublicenciar ou divulgar, total ou parcialmente,
            qualquer conteúdo da plataforma, em qualquer formato ou meio, sem autorização prévia e expressa da
            English2Empower.
          </li>
          <li>
            Utilizar os materiais da plataforma para fins comerciais, educacionais ou institucionais fora do ambiente da
            E2E.
          </li>
          <li>
            Disponibilizar os conteúdos para pessoas que não sejam estudantes regularmente matriculados(as) na escola.
          </li>
        </ul>
        <br />O uso dos materiais é <strong>exclusivo, pessoal e intransferível</strong>, destinado apenas ao(à)
        estudante cadastrado(a).
      </>
    ),
  },
  {
    title: '3. Expectativas de Uso e Convivência',
    description: (
      <>
        A English2Empower é uma comunidade de aprendizagem formada por pessoas com diferentes histórias, trajetórias e
        vivências. Ao utilizar a plataforma e participar das aulas, o(a) estudante compromete-se a contribuir para um
        ambiente seguro, respeitoso e colaborativo.
        <br />
        <br />
        <strong>a) Respeito à diversidade e à convivência</strong>
        <br />
        Todas as interações devem ser pautadas pelo respeito à diversidade de opiniões, experiências, sotaques,
        identidades e percursos de aprendizagem. Não são toleradas atitudes discriminatórias, ofensivas ou que comprometam
        a segurança emocional da comunidade.
        <br />
        <br />
        <strong>b) Compromisso e constância no processo de aprendizagem</strong>
        <br />O(a) estudante reconhece que aprender um idioma exige envolvimento ativo, estudo e prática contínua. O
        desenvolvimento linguístico depende de compromisso com as aulas, materiais e propostas pedagógicas, não existindo
        resultados imediatos ou fórmulas mágicas.
        <br />
        <br />
        <strong>c) Aprendizagem em comunidade</strong>
        <br />A E2E valoriza a aprendizagem coletiva como parte essencial do processo educacional. Espera-se uma postura
        colaborativa, respeitosa e solidária, que contribua para o crescimento individual e coletivo dos(as) estudantes.
      </>
    ),
  },
  {
    title: '4. Integridade Acadêmica',
    description: (
      <>
        As atividades, exercícios, avaliações e feedbacks disponibilizados na plataforma da English2Empower são
        desenvolvidos para uso <strong>individual e exclusivo</strong> de cada estudante, de acordo com seu nível e
        percurso de aprendizagem.
        <br />
        <br />
        É proibido:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            Compartilhar respostas, atividades, exercícios, transcrições, feedbacks ou qualquer conteúdo avaliativo da
            plataforma com terceiros.
          </li>
          <li>Utilizar respostas ou materiais produzidos por outros(as) estudantes como se fossem próprios.</li>
          <li>Copiar, reproduzir ou redistribuir atividades avaliativas fora da plataforma.</li>
        </ul>
        <br />
        Essas práticas comprometem o aprendizado individual, o funcionamento pedagógico da escola e os princípios de
        honestidade e responsabilidade acadêmica da English2Empower.
      </>
    ),
  },
  {
    title: '5. Acesso à Plataforma e Uso de Login',
    description: (
      <>
        O acesso à plataforma da English2Empower é realizado por meio de login e senha individuais, criados de forma
        exclusiva para cada estudante cadastrado(a).
        <br />
        <br />
        É <strong>terminantemente proibido</strong>:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Compartilhar login, senha ou qualquer forma de acesso à plataforma com terceiros.</li>
          <li>
            Permitir que pessoas não matriculadas utilizem ou visualizem os conteúdos da plataforma por meio de seu
            acesso.
          </li>
        </ul>
        <br />
        Cada estudante é integralmente responsável pela confidencialidade de suas credenciais de acesso e por qualquer
        atividade realizada em sua conta.
      </>
    ),
  },
  {
    title: '6. Controle de Acesso e Monitoramento',
    description: (
      <>
        A English2Empower realiza o controle de acesso à plataforma por meio de <strong>logs individuais</strong>, que
        registram informações como data, horário e frequência de acesso de cada usuário(a).
        <br />
        <br />
        Esses registros têm como finalidade garantir a segurança da plataforma, proteger a propriedade intelectual da
        escola e verificar o cumprimento deste Termo de Uso.
        <br />
        <br />O compartilhamento de acessos poderá ser identificado por meio desses registros e caracterizado como
        violação contratual.
      </>
    ),
  },
  {
    title: '7. Sanções em Caso de Violação',
    description: (
      <>
        O descumprimento de qualquer cláusula deste Termo de Uso poderá resultar, a critério da English2Empower, nas
        seguintes medidas, aplicáveis de forma isolada ou cumulativa:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Bloqueio temporário ou definitivo do acesso à plataforma.</li>
          <li>Cancelamento da matrícula, sem direito a reembolso.</li>
          <li>
            Adoção de medidas legais cabíveis para proteção da propriedade intelectual e reparação de eventuais danos.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: '8. Disposições Finais',
    description: (
      <>
        A English2Empower reserva-se o direito de atualizar este Termo de Uso sempre que necessário, sendo responsabilidade
        do(a) usuário(a) manter-se informado(a) sobre eventuais alterações.
        <br />
        <br />
        Este Termo é regido pelas leis da República Federativa do Brasil.
      </>
    ),
  },
];

const DEFAULT_CONFIRMATION =
  'Ao selecionar “Li, compreendi e concordo”, você confirma que manterá a confidencialidade das informações de alunas e do conteúdo da English2Empower, e que o compartilhamento ou exportação de materiais fora da plataforma é proibido.';

const B2B_CONFIRMATION =
  'Ao selecionar “Li, compreendi e concordo”, você confirma que manterá a confidencialidade das informações de estudantes e do conteúdo da English2Empower, e que o compartilhamento ou exportação de materiais fora da plataforma é proibido.';

export function TermsAndConditionsDialog({
  open,
  onAccept,
  onDecline,
  sections,
  highlights,
  eyebrow = 'English2Empower',
  title = 'Termo de Uso da Plataforma',
  subtitle = 'Para continuar, por favor confirme que você leu e concorda com os termos abaixo. Eles garantem a segurança da nossa comunidade e a proteção do nosso conteúdo.',
  effectiveDate,
  supportEmail,
  acceptLabel = 'Li, compreendi e concordo',
  isAccepting = false,
}: TermsAndConditionsDialogProps) {
  // Cohort-aware default: b2b users get the neutral-professional variant of the
  // same terms; every other user (including while the profile is still loading)
  // keeps the default women-first text.
  const { isB2b } = useUserType();
  const resolvedSections = useMemo(
    () => sections ?? (isB2b ? B2B_SECTIONS : DEFAULT_SECTIONS),
    [sections, isB2b],
  );
  const resolvedHighlights = useMemo(() => highlights ?? DEFAULT_HIGHLIGHTS, [highlights]);
  const confirmationText = isB2b ? B2B_CONFIRMATION : DEFAULT_CONFIRMATION;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && open && onDecline) {
          onDecline();
        }
      }}
    >
      <DialogContent
        className="max-w-3xl border-none bg-transparent p-0 shadow-none outline-none"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <div className="overflow-hidden rounded-2xl border border-rose-50 bg-white shadow-2xl sm:rounded-[32px]">
          <div className="bg-gradient-to-br from-[#cf2e2e] via-[#f05f7a] to-[#f78da7] px-5 py-6 text-white sm:px-8 sm:py-8">
            <DialogHeader className="space-y-3 text-left sm:space-y-4">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/70 sm:text-xs sm:tracking-[0.3em]">
                {eyebrow}
              </p>
              <DialogTitle className="text-2xl font-semibold leading-snug sm:text-3xl lg:text-4xl">
                {title}
              </DialogTitle>
              <DialogDescription className="text-sm text-white/85 sm:text-base">
                {subtitle}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-5 flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] sm:mt-6 sm:gap-3 sm:text-xs sm:tracking-[0.25em]">
              {resolvedHighlights.map((item) => (
                <span key={item} className="rounded-full bg-white/20 px-3 py-1.5 sm:px-4 sm:py-2">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="max-h-[65vh] overflow-y-auto px-5 py-6 sm:max-h-[70vh] sm:px-10 sm:py-8">
            <div className="space-y-8">
              {resolvedSections.map((section, index) => (
                <div key={section.title} className="space-y-3">
                  <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.12em] text-[#cf2e2e]">
                    <span className="h-2 w-2 rounded-full bg-[#cf2e2e]" />
                    {section.title}
                  </div>
                  <div className="text-sm leading-relaxed text-gray-600 sm:text-[15px]">{section.description}</div>
                  {index < resolvedSections.length - 1 && <Separator className="border-rose-50" />}
                </div>
              ))}

              <div className="rounded-2xl border border-rose-100 bg-rose-50/50 p-4 text-sm text-rose-900 sm:rounded-[28px] sm:p-5">
                {confirmationText}
              </div>

              <div className="flex flex-col gap-4 pt-1 sm:flex-row sm:items-center sm:justify-between">
                {effectiveDate ? (
                  <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 sm:text-xs sm:tracking-[0.3em]">
                    Atualizado em {effectiveDate}
                  </p>
                ) : (
                  <span />
                )}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  {supportEmail && (
                    <a
                      href={`mailto:${supportEmail}`}
                      className="text-sm font-semibold text-[#cf2e2e] underline-offset-4 hover:underline"
                    >
                      Dúvidas? Fale com o suporte
                    </a>
                  )}
                  <Button
                    onClick={onAccept}
                    disabled={isAccepting}
                    className="rounded-full bg-[#cf2e2e] px-5 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-[#b82727] disabled:cursor-not-allowed disabled:opacity-70 sm:px-6 sm:tracking-[0.25em]"
                  >
                    {isAccepting ? 'Salvando...' : acceptLabel}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TermsAndConditionsDialog;
